import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../core/breadcrumbs";
import { apiClient } from "../../services/apiClient";
import { useToast } from "../../Toaster/ToastContext";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

applyPlugin(jsPDF);

const ExpenseReport = () => {
  const { showToast } = useToast();
  const [expenseData, setExpenseData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date(),
  });

  const fetchExpenseReport = async () => {
    try {
      const response = await apiClient.post(`/generateExpenseReport`, {
        type: "preview",
      });
      if (response?.success && Array.isArray(response.data)) {
        setExpenseData(response.data);
        setFilteredData(response.data); // Initialize filtered data
      } else {
        setExpenseData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Failed to fetch expense report:", error);
      showToast("Failed to fetch expense report", "error");
    }
  };

  useEffect(() => {
    fetchExpenseReport();
  }, []);

  // Handle search input change
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = expenseData.filter((expense) =>
      [
        expense.expense_name?.toLowerCase() || "",
      ].some((field) => field.includes(query))
    );
    setFilteredData(filtered);
  };

  const handleExportExcel = async () => {
    try {
      const response = await apiClient.post(`/generateExpenseReport`, {});
      const fileUrl = response?.path;

      if (fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = "expense-report.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Expense report exported to Excel successfully", "success");
      } else {
        showToast("No file URL found in response", "error");
      }
    } catch (error) {
      showToast("Failed to export expense report to Excel", "error");
    }
  };

  // Handle PDF export
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Header
      doc.setFontSize(20);
      doc.text("Expense Report", 14, 20);
      doc.setFontSize(12);
      doc.text(
        `Date Range: ${dateRange.startDate.toLocaleDateString("en-GB")} - ${dateRange.endDate.toLocaleDateString("en-GB")}`,
        14,
        30
      );
      doc.text("Generated on: July 30, 2025, 11:52 AM PKT", 14, 40);

      // Table
      doc.autoTable({
        startY: 50,
        head: [["Date", "Expense Category", "Expense Name", "Amount"]],
        body: filteredData.map((expense) => [
          formatDate(expense.date),
          expense.category || "N/A",
          expense.expense_name || "N/A",
          expense.amount ? `PKR ${parseFloat(expense.amount).toFixed(2)}` : "N/A",
        ]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [100, 100, 100], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 50 },
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: "right" });
      }

      // Save PDF
      doc.save("expense-report.pdf");
      showToast("Expense report exported to PDF successfully", "success");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      showToast("Failed to export expense report to PDF", "error");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Render expense rows
  const renderExpenseRows = () => {
    return filteredData.map((expense, index) => (
      <tr key={index}>
        <td><span className="text-wrap">{formatDate(expense.date)}</span></td>
        <td><span className="text-wrap">{expense.category || "N/A"}</span></td>
        <td><span className="text-wrap">{expense.expense_name || "N/A"}</span></td>
        <td><span className="text-wrap">{expense.amount ? `PKR ${parseFloat(expense.amount).toFixed(2)}` : "N/A"}</span></td>
      </tr>
    ));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Expense Report"
          subtitle="Manage Your Expense Report"
          onExcelClick={handleExportExcel}
          onPDFClick={handleExportPDF}
        />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search by expense name..."
                    className="form-control form-control-sm formsearch"
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table datanew">
                <thead>
                  <tr>
                    <th style={{ width: "15%" }}>Date</th>
                    <th style={{ width: "30%" }}>Expense Category</th>
                    <th style={{ width: "30%" }}>Expense Name</th>
                    <th style={{ width: "20%" }}>Amount</th>
                  </tr>
                </thead>
                <tbody className="Expense-list">
                  {filteredData.length > 0 ? (
                    renderExpenseRows()
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReport;